import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { ArrowRight, ShieldCheck, AlertCircle, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";

const INITIAL = {
  patient_type: "child",
  age: "",
  full_name: "",
  mobile: "",
  email: "",
  city: "",
  state: "",
  main_concern: "",
  hearing_loss_duration: "",
  current_diagnosis: "",
  current_device: "none",
  hearing_aid_helping: "",
  speech_status: "",
  preferred_language: "English",
  preferred_callback_time: "",
  notes: "",
  consent: false,
  source_page: "eligibility",
};

export default function Eligibility() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fileUploaded, setFileUploaded] = useState(null);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the consent checkbox.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, age: parseInt(form.age, 10) };
      const { data } = await api.post("/eligibility", payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Assessment submitted. A counsellor will reach out soon.");
    } catch (err) {
      toast.error(formatApiError(err, "Submission failed. Try again."));
    }
    setLoading(false);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileUploaded(data.filename);
      toast.success("Report uploaded.");
    } catch (err) {
      toast.error("Upload failed.");
    }
  };

  if (result) {
    return (
      <>
        <SEO title="Your Cochlear Implant Eligibility Assessment" />
        <section className="hc-container py-14">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[#0F766E] font-semibold uppercase text-xs tracking-wider mb-3">
              <ShieldCheck size={16} /> Assessment complete
            </div>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              {result.eligibility_result}
            </h1>
            <p className="mt-4 text-slate-700 text-[17px]">{result.result_text}</p>

            <div className="mt-8 bg-gradient-to-br from-[#F0F9FF] to-[#ECFDF5] border border-[#0369A1]/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <span className="text-[#0F766E] font-semibold uppercase tracking-wider text-xs">Indicative score</span>
                <span className="text-2xl font-serif font-semibold text-[#0369A1]">{result.eligibility_score} / 100</span>
              </div>
              <div className="mt-3 h-2.5 bg-white rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#0369A1] to-[#0F766E]" style={{ width: `${result.eligibility_score}%` }} />
              </div>
            </div>

            {result.reasons?.length > 0 && (
              <div className="mt-8">
                <h2 className="font-serif text-xl font-semibold text-slate-900">Why this result</h2>
                <ul className="mt-3 space-y-2">
                  {result.reasons.map((r, i) => (
                    <li key={i} className="flex gap-2 text-slate-700"><CheckCircle2 size={18} className="text-[#0F766E] mt-1 shrink-0" /> {r}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.next_steps?.length > 0 && (
              <div className="mt-8">
                <h2 className="font-serif text-xl font-semibold text-slate-900">Recommended next steps</h2>
                <ul className="mt-3 space-y-2">
                  {result.next_steps.map((r, i) => (
                    <li key={i} className="flex gap-2 text-slate-700"><ArrowRight size={18} className="text-[#0369A1] mt-1 shrink-0" /> {r}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-10 bg-amber-50 border border-amber-200 rounded-xl p-5 text-amber-900 text-[14.5px] flex gap-3">
              <AlertCircle size={20} className="shrink-0 mt-0.5" />
              <div><strong>Disclaimer:</strong> {result.disclaimer || "This is not a diagnosis. Please consult a qualified specialist."}</div>
            </div>

            <button
              data-testid="restart-eligibility"
              onClick={() => { setResult(null); setForm(INITIAL); }}
              className="mt-8 btn-secondary"
            >
              Submit another assessment
            </button>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Free Cochlear Implant Eligibility Assessment"
        description="Free, confidential cochlear implant eligibility assessment for children and adults in India. Upload your audiogram and get guidance from HearClear's clinical team."
      />
      <section className="bg-gradient-to-br from-[#F0F9FF] via-white to-[#ECFDF5] py-14">
        <div className="hc-container">
          <h1 className="font-serif text-3xl md:text-[2.75rem] font-semibold leading-tight text-slate-900 max-w-3xl">
            Free Cochlear Implant Eligibility Assessment
          </h1>
          <p className="mt-4 text-slate-700 text-[1.05rem] max-w-2xl">
            Confidential. No cost. A HearClear counsellor will personally review your information
            and guide you on the next steps. This is not a diagnosis.
          </p>
        </div>
      </section>

      <section className="hc-container py-14">
        <form data-testid="eligibility-form" onSubmit={submit} className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
              <h2 className="font-serif text-2xl font-semibold text-slate-900">Patient information</h2>

              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-hc">Patient type *</label>
                  <select data-testid="patient-type" required className="input-hc" value={form.patient_type} onChange={(e) => update("patient_type", e.target.value)}>
                    <option value="child">Child</option>
                    <option value="adult">Adult</option>
                  </select>
                </div>
                <div>
                  <label className="label-hc">Age (years) *</label>
                  <input data-testid="age-input" required type="number" min={0} max={120} className="input-hc" value={form.age} onChange={(e) => update("age", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-hc">Patient / Parent name *</label>
                  <input data-testid="name-input" required className="input-hc" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} />
                </div>
                <div>
                  <label className="label-hc">Mobile number *</label>
                  <input data-testid="mobile-input" required className="input-hc" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} placeholder="+91 …" />
                </div>
                <div>
                  <label className="label-hc">Email (optional)</label>
                  <input data-testid="email-input" type="email" className="input-hc" value={form.email} onChange={(e) => update("email", e.target.value)} />
                </div>
                <div>
                  <label className="label-hc">City *</label>
                  <input data-testid="city-input" required className="input-hc" value={form.city} onChange={(e) => update("city", e.target.value)} />
                </div>
                <div>
                  <label className="label-hc">State</label>
                  <input data-testid="state-input" className="input-hc" value={form.state} onChange={(e) => update("state", e.target.value)} />
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
              <h2 className="font-serif text-2xl font-semibold text-slate-900">Hearing concern</h2>

              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="label-hc">Main concern</label>
                  <textarea data-testid="concern-input" className="input-hc" rows={2} value={form.main_concern} onChange={(e) => update("main_concern", e.target.value)} placeholder="e.g. Child not responding to sounds since birth" />
                </div>
                <div>
                  <label className="label-hc">Hearing loss duration</label>
                  <input data-testid="duration-input" className="input-hc" value={form.hearing_loss_duration} onChange={(e) => update("hearing_loss_duration", e.target.value)} placeholder="e.g. since birth, 2 years…" />
                </div>
                <div>
                  <label className="label-hc">Current diagnosis (if any)</label>
                  <input data-testid="diagnosis-input" className="input-hc" value={form.current_diagnosis} onChange={(e) => update("current_diagnosis", e.target.value)} placeholder="e.g. bilateral profound SNHL" />
                </div>
                <div>
                  <label className="label-hc">Currently using</label>
                  <select data-testid="device-input" className="input-hc" value={form.current_device} onChange={(e) => update("current_device", e.target.value)}>
                    <option value="none">No device</option>
                    <option value="hearing_aid">Hearing aid</option>
                    <option value="previous_ci">Previous cochlear implant</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="label-hc">Is hearing aid helping?</label>
                  <select data-testid="ha-helping-input" className="input-hc" value={form.hearing_aid_helping} onChange={(e) => update("hearing_aid_helping", e.target.value)}>
                    <option value="">Select…</option>
                    <option value="yes">Yes — clearly helping</option>
                    <option value="partial">Partial benefit</option>
                    <option value="no">No / minimal benefit</option>
                    <option value="na">Not applicable</option>
                  </select>
                </div>
                {form.patient_type === "child" && (
                  <div className="sm:col-span-2">
                    <label className="label-hc">Speech development</label>
                    <input data-testid="speech-input" className="input-hc" value={form.speech_status} onChange={(e) => update("speech_status", e.target.value)} placeholder="e.g. no speech, words only, sentences…" />
                  </div>
                )}
                <div className="sm:col-span-2">
                  <label className="label-hc">Upload audiogram / BERA / ASSR report (PDF / image, optional)</label>
                  <input data-testid="file-upload" type="file" accept="image/*,application/pdf" onChange={uploadFile} className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-[#F0F9FF] file:text-[#0369A1] file:px-4 file:py-2 file:font-medium" />
                  {fileUploaded && <div className="mt-2 text-[13px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={14} /> Uploaded</div>}
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
              <h2 className="font-serif text-2xl font-semibold text-slate-900">Preferences & consent</h2>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-hc">Preferred language</label>
                  <select data-testid="lang-input" className="input-hc" value={form.preferred_language} onChange={(e) => update("preferred_language", e.target.value)}>
                    {["English", "Hindi", "Tamil", "Telugu", "Marathi", "Bengali", "Kannada", "Malayalam", "Gujarati", "Punjabi", "Other"].map((l) => <option key={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-hc">Preferred callback time</label>
                  <input data-testid="callback-input" className="input-hc" value={form.preferred_callback_time} onChange={(e) => update("preferred_callback_time", e.target.value)} placeholder="e.g. weekdays 10am–1pm" />
                </div>
              </div>

              <label className="mt-5 flex items-start gap-3 cursor-pointer">
                <input data-testid="consent-input" required type="checkbox" className="mt-1 w-5 h-5 accent-[#0369A1]" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} />
                <span className="text-slate-700 text-[15px] leading-relaxed">
                  I consent to HearClear contacting me for cochlear implant guidance and storing my
                  information securely. I understand this assessment is not a diagnosis.
                </span>
              </label>

              <button
                data-testid="eligibility-submit"
                type="submit"
                disabled={loading}
                className="btn-primary mt-6 w-full sm:w-auto"
              >
                {loading ? "Submitting…" : "Get my eligibility result"} <ArrowRight size={18} className="ml-1.5" />
              </button>
            </div>
          </div>

          <aside className="space-y-5">
            <div className="bg-gradient-to-br from-[#0369A1] to-[#0F766E] text-white rounded-2xl p-6">
              <div className="font-serif text-xl font-semibold">What happens next?</div>
              <ol className="mt-4 space-y-3 text-[14.5px] text-sky-50">
                <li><strong>1.</strong> Your information is reviewed by a HearClear counsellor.</li>
                <li><strong>2.</strong> A callback is arranged at your preferred time.</li>
                <li><strong>3.</strong> You receive personalised guidance on tests, doctors and funding.</li>
              </ol>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="font-serif text-lg font-semibold text-slate-900">Your privacy</div>
              <p className="mt-2 text-slate-600 text-[14px] leading-relaxed">
                Reports you upload are not public. Your contact information is used only for
                counselling and is never sold. You may request data deletion at any time.
              </p>
            </div>
          </aside>
        </form>
      </section>
    </>
  );
}
